import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useProductCenterApi } from 'api';
import { getUrlHealthCheckPDFDownload } from 'api/ApiUrls';
import { getSupportedLanguagesQuery } from 'api/queries';
import { getHealthCheckQuery } from 'api/queries/HealthCheckQueries';
import { useAdobeTracking } from 'library/hooks/useAdobeTracking';
import { getClientTimeInISO } from 'library/utils/dateTime/clientTime';
import { vppnReverseProxyType } from 'main/auth/provider';
import {
	PdfPrintFormLayout,
	PdfPrintSwitchConfig,
	PdfPrintTab,
} from 'modules/Shared/PdfPrint/PdfPrintFormLayout';
import { PrintPdfDialogContentProps } from 'modules/Shared/PdfPrint/types';
import { usePdfDownloadSubmit } from 'modules/Shared/PdfPrint/usePdfDownloadSubmit';
import { usePdfPreview } from 'modules/Shared/PdfPrint/usePdfPreview';
import { useStorePdfUserSettings } from 'modules/Shared/PdfPrint/usePdfUserSettings';

import { PrintableAttachments } from './PrintableAttachments';
import {
	HEALTH_CHECK_PDF_DEFAULTS,
	PrintHealthCheckUserSettings,
	mapFormToHealthCheckPdfRequest,
} from './Types';
import { healthCheckPdfSettingsCategories } from './healthCheckSettingsCategory';

type PrintHealthCheckForm = PrintHealthCheckUserSettings & {
	additionalText?: string;
	language: string;
};

const normalizeLanguage = (code: string) =>
	code.replace(/-/g, '').toLowerCase();

export const HealthCheckDialogContent = ({
	id,
	onClose,
	previewDebounce = 1500,
	defaultPdfSettings,
	isDealer,
	setIsDealer,
	isPreview,
	setIsPreview,
}: PrintPdfDialogContentProps<PrintHealthCheckUserSettings>) => {
	const { t, i18n } = useTranslation([
		'commonButton',
		'serviceProtocol',
		'healthCheck',
		'common',
		'language',
	]);
	const { api } = useProductCenterApi();
	const { trackFormSubmitSuccess } = useAdobeTracking();
	const updatePdfSettings = useStorePdfUserSettings(
		healthCheckPdfSettingsCategories
	);

	const defaultValues =
		(isDealer
			? defaultPdfSettings?.forDealer
			: defaultPdfSettings?.forCustomer) ?? HEALTH_CHECK_PDF_DEFAULTS;

	const { control, getValues, setValue, subscribe, handleSubmit } =
		useForm<PrintHealthCheckForm>({
			mode: 'onChange',
			defaultValues: { ...defaultValues, language: i18n.language },
		});

	const { data: supportedLanguages } = useQuery(
		getSupportedLanguagesQuery(api)
	);

	const { data: healthCheckData } = useQuery(getHealthCheckQuery(api, id));

	const attachments = useMemo(
		() =>
			healthCheckData?.files.filter(
				(file) => file.fileType === 'Attachment'
			) ?? [],
		[healthCheckData]
	);

	const languageOptions = useMemo(
		() =>
			Object.entries(supportedLanguages ?? {}).map(([name, code]) => ({
				id: code,
				label: t(`language:changeLanguage-${code.toLowerCase()}`, {
					defaultValue: name,
				}),
			})),
		[supportedLanguages, t]
	);

	const buildRequest = useCallback(
		() =>
			mapFormToHealthCheckPdfRequest(
				id,
				getValues(),
				vppnReverseProxyType(),
				isDealer,
				getValues().additionalText,
				getValues().language
			),
		[id, getValues, isDealer]
	);

	const getFileName = useCallback(
		() => `Health Check ${getClientTimeInISO()}.pdf`,
		[]
	);

	const { previewUrl, isLoadingPreview, triggerPreview } = usePdfPreview({
		generate: (signal) =>
			api
				.downloadHealthCheckPdf(
					buildRequest(),
					{ openFile: false },
					{ format: 'blob', signal }
				)
				.then((response) => response.data),
		getFileName,
		previewDebounce,
		onError: () => setIsPreview(false),
		disabled: !isPreview,
	});

	const { submit, isPending } = usePdfDownloadSubmit<PrintHealthCheckForm>({
		url: getUrlHealthCheckPDFDownload,
		getFileName,
		buildFormData: buildRequest,
		onSuccess: () => {
			void trackFormSubmitSuccess({
				type: 'health-check',
				name: 'Print Health Check',
				id: id.toString(),
				destination: 'pdf-download',
				topic: isDealer ? 'dealer' : 'customer',
			});
		},
	});

	const onTabChange = (_: React.SyntheticEvent, newValue: PdfPrintTab) => {
		void updatePdfSettings(getValues(), newValue !== 'dealer'); // save settings for the tab we are leaving
		setIsDealer(newValue === 'dealer');
	};

	useEffect(() => {
		// refresh the preview on any field change (switches, language select,
		// comment box). Driving this off react-hook-form's subscribe API rather
		// than the form's DOM onChange ensures non-native inputs like the MUI
		// language Select are covered too. subscribe is a side-effect listener
		// and does not trigger re-renders.
		if (!isPreview) {
			return undefined;
		}
		return subscribe({
			formState: { values: true },
			callback: () => triggerPreview(),
		});
	}, [subscribe, isPreview, triggerPreview]);

	useEffect(() => {
		// covers turning preview on and switching tabs while preview is active
		if (isPreview) {
			triggerPreview();
		}
	}, [isPreview, isDealer, triggerPreview]);

	useEffect(() => {
		// align the preselected language with the active UI language once the
		// supported language list resolves
		const match = languageOptions.find(
			(option) =>
				normalizeLanguage(option.id) ===
				normalizeLanguage(i18n.language)
		);
		if (match && getValues('language') !== match.id) {
			setValue('language', match.id);
		}
	}, [languageOptions, i18n.language, getValues, setValue]);

	const onSubmit = handleSubmit((data) => {
		void updatePdfSettings(data, isDealer);
		void submit(data);
	});

	const onSubmitHandler = (event: React.SubmitEvent<HTMLFormElement>) => {
		void onSubmit(event);
	};

	const switches = useMemo<PdfPrintSwitchConfig<PrintHealthCheckForm>[]>(
		() => [
			...(isDealer
				? [
						{
							name: 'printQr' as const,
							label: t('healthCheck:healthCheckPrintout-qr'),
							helperText: t(
								'healthCheck:healthCheckPrintout-qr-helper'
							),
						},
					]
				: []),
			{
				name: 'printEstimatedHours',
				label: t('healthCheck:healthCheckPrintout-estimatedHours'),
			},
			{
				name: 'printUsedHours',
				label: t('healthCheck:healthCheckPrintout-usedHours'),
			},
			{
				name: 'printConfiguration',
				label: t('healthCheck:healthCheckPrintout-configuration'),
			},
			{
				name: 'printEndCustomer',
				label: t('healthCheck:healthCheckPrintout-endCustomer'),
			},
			{
				name: 'printCampaigns',
				label: t('healthCheck:healthCheckPrintout-campaigns'),
			},
			{
				name: 'printCoverage',
				label: t('healthCheck:healthCheckPrintout-coverages'),
			},
			{
				name: 'printNotes',
				label: t('healthCheck:healthCheckPrintout-additionalNotes'),
			},
			{
				name: 'printRecommendedActions',
				label: t('healthCheck:healthCheckPrintout-recommendedActions'),
			},
			{
				name: 'printSignatures',
				label: t('healthCheck:healthCheckPrintout-signatures'),
			},
			{
				name: 'printAttachedFiles',
				label: t('healthCheck:healthCheckPrintout-attachedFiles'),
			},
		],
		[isDealer, t]
	);

	return (
		<>
			<PdfPrintFormLayout
				control={control}
				onSubmit={onSubmitHandler}
				isPreview={isPreview}
				isPending={isPending}
				description={t('healthCheck:healthCheckPrintout-info-content')}
				dealerTabLabel={t('healthCheck:healthCheckPrintout-forDealer')}
				customerTabLabel={t(
					'healthCheck:healthCheckPrintout-forCustomer'
				)}
				isDealer={isDealer}
				onTabChange={onTabChange}
				additionalInfoTitle={t(
					'serviceProtocol:protocolPrintout-additionalInformation'
				)}
				commentBoxLabel={t(
					'serviceProtocol:protocolPrintout-additionalInformation-alt'
				)}
				languageSelect={{
					name: 'language',
					label: t('healthCheck:healthCheckPrintout-language'),
					options: languageOptions,
				}}
				switchSectionLabel={t(
					'healthCheck:healthCheckPrintout-switchLabel'
				)}
				switches={switches}
				preview={{
					isLoadingPreview: isLoadingPreview,
					previewUrl: previewUrl,
					objectName: 'HealthCheckPdf',
					generateButtonLabel: t(
						'healthCheck:healthCheckPrintout-generate-preview-button'
					),
					onGenerateClick: () => setIsPreview(true),
				}}
				onClose={onClose}
			/>
			<PrintableAttachments attachments={attachments} />
		</>
	);
};
